import React, { useEffect, useRef } from 'react';
import { 
  Target, 
  Users, 
  MessageCircle, 
  Briefcase, 
  Heart, 
  Star,
  Unlock,
  TrendingUp,
  BarChart3,
  Shield
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import SectionHeader from './SectionHeader';

const features = [
  {
    icon: Target,
    title: 'Smart Job Matching',
    description: 'AI-powered algorithm matches freelancers with perfect job opportunities based on skills and preferences.',
    gradient: 'from-blue-500 to-cyan-400',
    bgColor: 'bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm'
  },
  {
    icon: Users,
    title: 'Freelancer Profiles',
    description: 'Showcase your work with detailed profiles, portfolios, and skill highlights to attract clients.',
    gradient: 'from-cyan-500 to-teal-400',
    bgColor: 'bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm'
  },
  {
    icon: MessageCircle,
    title: 'Real-time Chat',
    description: 'Instant messaging with clients and freelancers. Send proposals and negotiate terms seamlessly.',
    gradient: 'from-teal-500 to-green-400',
    bgColor: 'bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm'
  },
  {
    icon: Briefcase,
    title: 'Job Posting',
    description: 'Post jobs easily and hire the best talent. Complete project management from start to finish.',
    gradient: 'from-green-500 to-emerald-400',
    bgColor: 'bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm'
  },
  {
    icon: Heart,
    title: 'Community Feed',
    description: 'Engage with posts, likes, and comments. Build your network and stay connected.',
    gradient: 'from-pink-500 to-rose-400',
    bgColor: 'bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm'
  },
  {
    icon: Star,
    title: 'Ratings & Reviews',
    description: 'Build trust with verified ratings and reviews from real clients and freelancers.',
    gradient: 'from-amber-500 to-orange-400',
    bgColor: 'bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm'
  }
];

const Features = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.feature-card');
            cards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-fade-in-up');
              }, index * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="features"
      className="py-12 md:py-12 relative overflow-hidden"
      ref={sectionRef}
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 right-0 h-80 w-80 rounded-full bg-cyan-500/10 blur-[120px]"></div>
        <div className="absolute bottom-0 left-0 h-80 w-80 rounded-full bg-blue-500/10 blur-[120px]"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <SectionHeader
          eyebrow="Platform Features"
          title="Everything You Need to"
          accent="Succeed as a Freelancer"
          description="Powerful features designed to help freelancers and clients connect, collaborate, and grow together."
          className="mb-14 md:mb-16"
        />

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <Card
                key={index}
                className={`feature-card opacity-0 group relative overflow-hidden border border-slate-200/80 dark:border-white/10 hover:border-cyan-200 dark:hover:border-cyan-700/80 transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl ${feature.bgColor}`}
              >
                {/* Number Badge */}
                <div className="absolute top-4 right-4">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                    {index + 1}
                  </div>
                </div>
                
                <CardContent className="p-8 relative z-10">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-xl`}>
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-cyan-400 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>

                {/* Hover Gradient Effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
              </Card>
            );
          })}
        </div>

        {/* Additional Features */}
        <div className="mt-16 grid gap-6 md:grid-cols-4 lg:mt-20">
          <div className="text-center p-6 bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm rounded-2xl border border-slate-200/80 dark:border-white/10">
            <Unlock className="w-12 h-12 mx-auto mb-3 text-purple-600 dark:text-purple-400" />
            <h4 className="font-bold text-gray-900 dark:text-white mb-2">Chat Unlock</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">Premium subscriptions</p>
          </div>
          <div className="text-center p-6 bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm rounded-2xl border border-slate-200/80 dark:border-white/10">
            <TrendingUp className="w-12 h-12 mx-auto mb-3 text-indigo-600 dark:text-indigo-400" />
            <h4 className="font-bold text-gray-900 dark:text-white mb-2">Boost Posts</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">Increase visibility</p>
          </div>
          <div className="text-center p-6 bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm rounded-2xl border border-slate-200/80 dark:border-white/10">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 text-cyan-600 dark:text-cyan-400" />
            <h4 className="font-bold text-gray-900 dark:text-white mb-2">Analytics</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">Track performance</p>
          </div>
          <div className="text-center p-6 bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm rounded-2xl border border-slate-200/80 dark:border-white/10">
            <Shield className="w-12 h-12 mx-auto mb-3 text-green-600 dark:text-green-400" />
            <h4 className="font-bold text-gray-900 dark:text-white mb-2">Secure</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">Enterprise-grade</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
